import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Switch,
  FormControlLabel,
  Grid,
  InputAdornment
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { couponService, authService } from '../../services';

const CouponManagement = () => {
  // State for coupons data
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // State for dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' or 'edit'
  const [currentCoupon, setCurrentCoupon] = useState(null);
  
  // Form state
  const [code, setCode] = useState('');
  const [type, setType] = useState('percentage');
  const [value, setValue] = useState('');
  const [maxDiscount, setMaxDiscount] = useState('');
  const [minBookingAmount, setMinBookingAmount] = useState('');
  const [validFrom, setValidFrom] = useState(new Date());
  const [validTo, setValidTo] = useState(new Date(new Date().setMonth(new Date().getMonth() + 1)));
  const [usageLimit, setUsageLimit] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [description, setDescription] = useState('');
  const [bookingType, setBookingType] = useState('all');
  
  // Check if user is admin
  const isAdmin = authService.hasRole('admin');
  const isManager = authService.hasRole('manager');
  const hasAccess = isAdmin || isManager;
  
  // Fetch coupons on component mount
  useEffect(() => {
    if (!hasAccess) {
      setError('You do not have permission to access this page.');
      return;
    }
    
    fetchCoupons();
  }, [hasAccess]);
  
  // Fetch all coupons
  const fetchCoupons = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const couponsData = await couponService.fetchAllCoupons();
      setCoupons(couponsData);
    } catch (err) {
      console.error('Error fetching coupons:', err);
      setError('Failed to load coupons. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Generate a random coupon code
  const generateCouponCode = () => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    setCode(result);
  };
  
  // Open dialog for creating a new coupon
  const handleOpenCreateDialog = () => {
    setDialogMode('create');
    setCode('');
    setType('percentage');
    setValue('');
    setMaxDiscount('');
    setMinBookingAmount('');
    setValidFrom(new Date());
    setValidTo(new Date(new Date().setMonth(new Date().getMonth() + 1)));
    setUsageLimit('');
    setIsActive(true);
    setDescription('');
    setBookingType('all');
    setCurrentCoupon(null);
    setOpenDialog(true);
  };
  
  // Open dialog for editing an existing coupon
  const handleOpenEditDialog = (coupon) => {
    setDialogMode('edit');
    setCode(coupon.code);
    setType(coupon.type);
    setValue(coupon.value.toString());
    setMaxDiscount(coupon.maxDiscount ? coupon.maxDiscount.toString() : '');
    setMinBookingAmount(coupon.minBookingAmount ? coupon.minBookingAmount.toString() : '');
    setValidFrom(new Date(coupon.validFrom));
    setValidTo(new Date(coupon.validTo));
    setUsageLimit(coupon.usageLimit ? coupon.usageLimit.toString() : '');
    setIsActive(coupon.isActive);
    setDescription(coupon.description || '');
    setBookingType(coupon.bookingType || 'all');
    setCurrentCoupon(coupon);
    setOpenDialog(true);
  };
  
  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    if (!code || !type || !value || !validFrom || !validTo) {
      setError('Please fill in all required fields.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const couponData = {
        code,
        type,
        value: parseFloat(value),
        maxDiscount: maxDiscount ? parseFloat(maxDiscount) : undefined,
        minBookingAmount: minBookingAmount ? parseFloat(minBookingAmount) : undefined,
        validFrom,
        validTo,
        usageLimit: usageLimit ? parseInt(usageLimit) : undefined,
        isActive,
        description: description || undefined,
        bookingType: bookingType || 'all'
      };
      
      if (dialogMode === 'create') {
        await couponService.createCoupon(couponData);
      } else {
        await couponService.updateCoupon(currentCoupon.id, couponData);
      }
      
      // Refresh coupons list
      await fetchCoupons();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving coupon:', err);
      setError(err.response?.data?.message || 'Failed to save coupon. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle coupon deletion
  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await couponService.deleteCoupon(couponId);
      // Refresh coupons list
      await fetchCoupons();
    } catch (err) {
      console.error('Error deleting coupon:', err);
      setError(err.response?.data?.message || 'Failed to delete coupon. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Copy coupon code to clipboard
  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    alert(`Coupon code ${code} copied to clipboard!`);
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };
  
  // Check if coupon is expired
  const isExpired = (validTo) => {
    return new Date(validTo) < new Date();
  };
  
  // Get status label and color
  const getCouponStatus = (coupon) => {
    if (!coupon.isActive) {
      return { label: 'Inactive', color: 'default' };
    }
    if (isExpired(coupon.validTo)) {
      return { label: 'Expired', color: 'error' };
    }
    if (coupon.usageLimit && coupon.usageCount >= coupon.usageLimit) {
      return { label: 'Exhausted', color: 'warning' };
    }
    return { label: 'Active', color: 'success' };
  };
  
  // If not admin or manager, show access denied
  if (!hasAccess) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access denied. You must be an admin or manager to view this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          Coupon Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
          disabled={loading}
        >
          Create New Coupon
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {loading && !openDialog ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Validity</TableCell>
                <TableCell>Usage</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {coupons.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No coupons found.
                  </TableCell>
                </TableRow>
              ) : (
                coupons.map((coupon) => {
                  const status = getCouponStatus(coupon);
                  return (
                    <TableRow key={coupon.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" fontWeight="bold">
                            {coupon.code}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleCopyCode(coupon.code)}
                            sx={{ ml: 1 }}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        {coupon.description && (
                          <Typography variant="caption" color="text.secondary">
                            {coupon.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {coupon.type === 'percentage' ? 'Percentage' : 'Fixed Amount'}
                      </TableCell>
                      <TableCell>
                        {coupon.type === 'percentage' ? `${coupon.value}%` : `₹${coupon.value}`}
                        {coupon.maxDiscount && coupon.type === 'percentage' && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Max: ₹{coupon.maxDiscount}
                          </Typography>
                        )}
                        {coupon.minBookingAmount && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Min booking: ₹{coupon.minBookingAmount}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {formatDate(coupon.validFrom)} - {formatDate(coupon.validTo)}
                      </TableCell>
                      <TableCell>
                        {coupon.usageCount || 0} 
                        {coupon.usageLimit ? ` / ${coupon.usageLimit}` : ''}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={status.label}
                          color={status.color}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          color="primary"
                          onClick={() => handleOpenEditDialog(coupon)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteCoupon(coupon.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Create/Edit Coupon Dialog */}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {dialogMode === 'create' ? 'Create New Coupon' : 'Edit Coupon'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TextField
                    required
                    fullWidth
                    id="code"
                    label="Coupon Code"
                    name="code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    disabled={loading || (dialogMode === 'edit')}
                    sx={{ mr: 1 }}
                  />
                  {dialogMode === 'create' && (
                    <Button
                      variant="outlined"
                      onClick={generateCouponCode}
                      disabled={loading}
                    >
                      Generate
                    </Button>
                  )}
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="type-label">Discount Type</InputLabel>
                  <Select
                    labelId="type-label"
                    id="type"
                    value={type}
                    label="Discount Type"
                    onChange={(e) => setType(e.target.value)}
                    disabled={loading}
                  >
                    <MenuItem value="percentage">Percentage</MenuItem>
                    <MenuItem value="fixed">Fixed Amount</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  id="value"
                  label={type === 'percentage' ? 'Discount Percentage' : 'Discount Amount'}
                  name="value"
                  type="number"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  disabled={loading}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">{type === 'percentage' ? '%' : '₹'}</InputAdornment>,
                  }}
                />
              </Grid>
              
              {type === 'percentage' && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    id="maxDiscount"
                    label="Maximum Discount Amount"
                    name="maxDiscount"
                    type="number"
                    value={maxDiscount}
                    onChange={(e) => setMaxDiscount(e.target.value)}
                    disabled={loading}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                </Grid>
              )}
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="minBookingAmount"
                  label="Minimum Booking Amount"
                  name="minBookingAmount"
                  type="number"
                  value={minBookingAmount}
                  onChange={(e) => setMinBookingAmount(e.target.value)}
                  disabled={loading}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="usageLimit"
                  label="Usage Limit (blank for unlimited)"
                  name="usageLimit"
                  type="number"
                  value={usageLimit}
                  onChange={(e) => setUsageLimit(e.target.value)}
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Valid From"
                  value={validFrom}
                  onChange={(newValue) => setValidFrom(newValue)}
                  renderInput={(params) => <TextField {...params} fullWidth required />}
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Valid To"
                  value={validTo}
                  onChange={(newValue) => setValidTo(newValue)}
                  renderInput={(params) => <TextField {...params} fullWidth required />}
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="booking-type-label">Booking Type</InputLabel>
                  <Select
                    labelId="booking-type-label"
                    id="bookingType"
                    value={bookingType}
                    label="Booking Type"
                    onChange={(e) => setBookingType(e.target.value)}
                    disabled={loading}
                  >
                    <MenuItem value="all">All Bookings</MenuItem>
                    <MenuItem value="hotel">Hotel Only</MenuItem>
                    <MenuItem value="temple">Temple Only</MenuItem>
                    <MenuItem value="ghat">Ghat Only</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      disabled={loading}
                    />
                  }
                  label="Active"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="description"
                  label="Description"
                  name="description"
                  multiline
                  rows={2}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={loading}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading || !code || !type || !value || !validFrom || !validTo}
            >
              {loading ? <CircularProgress size={24} /> : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>
    </Box>
  );
};

export default CouponManagement;
